/**
 * The Norn cell API client (#2455), generated from the contract.
 *
 * `src/generated/norn-api.ts` is emitted by `openapi-typescript` from
 * `sdks/openapi/norn-api.openapi.json`, which `norn openapi` generates from
 * norn-api's typed routes (utoipa); CI regenerates both and fails on drift.
 * The runtime is `openapi-fetch`: a thin, typed `fetch` over those paths.
 * Nothing in this file restates a route or a schema.
 */
import createClient, { type Client, type ClientOptions } from "openapi-fetch"
import type { components, operations, paths } from "./generated/norn-api.ts"

export type { components, operations, paths }
export type Schemas = components["schemas"]

/** The kernel refusal taxonomy's categories (#2949): what the caller can do about a refusal. */
export type RefusalCategory = Schemas["RefusalCategory"]

/**
 * A refused request, discriminated by `category`. `code` is the stable
 * machine code (`scope.denied`, `action.precondition_failed`,
 * `lifecycle.validation.undeclared_write`, …).
 */
export type Refusal = {
  [C in RefusalCategory]: Omit<Schemas["Refusal"], "category"> & { readonly category: C }
}[RefusalCategory]

const CATEGORIES: ReadonlySet<string> = new Set<RefusalCategory>(["visibility", "permission", "precondition", "conflict", "invalid", "limit", "internal"] satisfies RefusalCategory[])

/** Whether a response body is a Norn refusal. */
export function isRefusal(value: unknown): value is Refusal {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  return typeof v.code === "string" && typeof v.message === "string" && typeof v.explanation === "string" && typeof v.category === "string" && CATEGORIES.has(v.category)
}

/** A refusal raised by {@link expectOk}. */
export class NornRefusal extends Error {
  constructor(
    readonly status: number,
    readonly refusal: Refusal,
  ) {
    super(`${status} ${refusal.code} (${refusal.category}): ${refusal.message}`)
  }
}

export interface NornClientOptions extends Omit<ClientOptions, "baseUrl"> {
  /** The cell's base URL (`http://127.0.0.1:8787`). */
  readonly baseUrl: string
  /** `x-norn-actor`: the local dev principal (norn dev). */
  readonly actor?: string
  /** A bearer token for a gated host. */
  readonly token?: string
}

export type NornClient = Client<paths>

/** A typed client for one cell. */
export function createNornClient(options: NornClientOptions): NornClient {
  const { actor, token, headers, ...rest } = options
  const extra: Record<string, string> = {}
  if (actor !== undefined) extra["x-norn-actor"] = actor
  if (token !== undefined) extra.authorization = `Bearer ${token}`
  return createClient<paths>({ ...rest, headers: { ...(headers as Record<string, string> | undefined), ...extra } })
}

/**
 * The refusal an error body carries: a bare refusal, or an action's
 * `{ accepted: false, refusal }` (`ActionRefused`).
 */
export function refusalOf(body: unknown): Refusal | undefined {
  if (isRefusal(body)) return body
  if (typeof body === "object" && body !== null && (body as { accepted?: unknown }).accepted === false) {
    const refusal = (body as { refusal?: unknown }).refusal
    if (isRefusal(refusal)) return refusal
  }
  return undefined
}

/**
 * The data of a successful response, or a {@link NornRefusal} (bare or
 * action refusal; an error body that is not a refusal is reported as is).
 */
export function expectOk<T>(result: { readonly data?: T; readonly error?: unknown; readonly response: Response }): T {
  if (result.error === undefined && result.data !== undefined) return result.data
  const refusal = refusalOf(result.error)
  if (refusal) throw new NornRefusal(result.response.status, refusal)
  throw new Error(`${result.response.status} ${result.response.statusText}: ${JSON.stringify(result.error ?? null)}`)
}

export {
  type ClientMessage,
  type DefinitionsUpdate,
  type ServerMessage,
  type SliceUpdate,
  type SlicePayload,
  type SocketFactory,
  type SocketLike,
  type SubscribeOptions,
  type SubscribeBindingOptions,
  type SubscribeDefinitionsOptions,
  type SubscriptionClientOptions,
  type SubscriptionHandle,
  type SubscriptionRefusal,
  type Timers,
  PROTOCOL_VERSION,
  SUBPROTOCOL,
  SubscriptionClient,
  connectSubscriptions,
  isDefinitionsUpdate,
  isSliceUpdate,
  applySliceUpdate,
} from "./subscriptions.ts"

export { parseSubscriptionJson, stringifySubscriptionJson } from "./subscription-content.ts"
